import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, Leaf, ArrowRight, Quote, Microscope, Apple, GraduationCap } from "lucide-react";
import api from "@/lib/api";
import useSeo from "@/lib/useSeo";

const ICONS = { "Nutritional Counseling": Apple, "Nutritional Microscopy": Microscope, "Health Education": GraduationCap };

function Hero({ slides }) {
  const [i, setI] = useState(0);
  const n = slides.length;
  const go = useCallback((d) => setI((p) => (p + d + n) % n), [n]);
  useEffect(() => {
    if (n <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n]);
  if (!n) return null;
  const s = slides[i];
  const isTel = (s.cta_url || "").startsWith("tel:");
  return (
    <section className="relative h-[92vh] min-h-[560px] w-full overflow-hidden" data-testid="hero-slideshow">
      {slides.map((sl, idx) => (
        <div key={sl.id} className="absolute inset-0 transition-opacity duration-1000"
          style={{ opacity: idx === i ? 1 : 0 }}>
          <img src={sl.image_url} alt={sl.headline} className="w-full h-full object-cover"
            style={{ objectPosition: sl.focal_point || "center" }} />
          <div className="absolute inset-0" style={{ background: `rgba(16,42,30,${(sl.overlay ?? 50) / 100})` }} />
        </div>
      ))}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
          <div className="max-w-2xl text-amber-50 animate-fade-up" key={i}>
            <span className="inline-flex items-center gap-2 text-sm font-medium bg-white/15 backdrop-blur px-4 py-1.5 rounded-full mb-5">
              <Leaf size={14} /> Natural Health & Wellness
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-[1.1] mb-5">{s.headline}</h1>
            <p className="text-lg text-stone-100/90 mb-8 max-w-xl">{s.message}</p>
            <div className="flex flex-wrap gap-3">
              {s.cta_url && (
                isTel
                  ? <a href={s.cta_url} data-testid="hero-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-[#C85A32] text-white px-7 py-3.5 font-semibold hover:bg-[#b24d28] transition-colors shadow-lg">{s.cta_label}</a>
                  : <Link to={s.cta_url} data-testid="hero-cta-btn" className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-emerald-950 px-7 py-3.5 font-semibold hover:bg-white transition-colors shadow-lg">{s.cta_label} <ArrowRight size={18} /></Link>
              )}
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-amber-50/50 text-amber-50 px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors">Contact Us</Link>
            </div>
          </div>
        </div>
      </div>
      {n > 1 && (
        <>
          <button onClick={() => go(-1)} data-testid="hero-slideshow-prev-btn" className="absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/35"><ChevronLeft /></button>
          <button onClick={() => go(1)} data-testid="hero-slideshow-next-btn" className="absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/35"><ChevronRight /></button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, idx) => <button key={idx} onClick={() => setI(idx)} className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-amber-50" : "w-2 bg-white/50"}`} />)}
          </div>
        </>
      )}
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  useSeo({ title: "Healthy Roots Holistic Practitioners Ltd", description: "Take back complete control of your health ...naturally! Holistic nutritional counseling, microscopy and health education in Hamilton, Ontario." });
  useEffect(() => { api.get("/public/home").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="h-screen flex items-center justify-center text-emerald-800">Loading…</div>;
  const phoneTel = data.settings?.phone_tel || "+12894421265";
  const phone = data.settings?.phone || "+1 (289) 442-1265";

  return (
    <div data-testid="home-page">
      <Hero slides={data.slides} />

      {/* Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-sm uppercase tracking-widest text-emerald-700 font-semibold">What We Offer</span>
          <h2 className="text-3xl sm:text-4xl mt-2 text-emerald-950">The Core of What We Offer</h2>
        </div>
        <div className="grid gap-7 md:grid-cols-3">
          {data.services.map((s) => {
            const Icon = ICONS[s.title] || Leaf;
            return (
              <div key={s.title} data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group bg-white rounded-3xl border border-stone-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden">
                <div className="h-48 overflow-hidden"><img src={s.image} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-7">
                  <span className="w-11 h-11 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center mb-4"><Icon size={20} /></span>
                  <h3 className="text-xl mb-2 text-emerald-900">{s.title}</h3>
                  <p className="text-sm text-stone-600 leading-relaxed mb-4">{s.summary}</p>
                  <Link to="/services" className="text-sm font-semibold text-emerald-700 inline-flex items-center gap-1 hover:gap-2 transition-all">Learn more <ArrowRight size={14} /></Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Meet the experts */}
      <section className="bg-emerald-950 text-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img src="https://healthyrootsholistics.com/wp-content/uploads/2021/09/founders-image.jpg" alt="Richard and Jasmine Loftman, founders of Healthy Roots" loading="lazy" className="rounded-3xl shadow-2xl w-full object-cover" onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=85&w=1200"; }} />
          </div>
          <div>
            <span className="text-sm uppercase tracking-widest text-emerald-400 font-semibold">Meet the Experts</span>
            <h2 className="text-3xl sm:text-4xl mt-2 mb-5 text-amber-50">Richard & Jasmine Loftman</h2>
            <p className="text-stone-300 leading-relaxed mb-7">{data.about_bio}</p>
            <ul className="space-y-2 mb-8">
              {data.core_offerings.map((o) => <li key={o} className="flex items-center gap-3 text-stone-200"><Leaf size={16} className="text-emerald-400" /> {o}</li>)}
            </ul>
            <Link to="/about-us" className="inline-flex items-center gap-2 rounded-full bg-amber-50 text-emerald-950 px-6 py-3 font-semibold hover:bg-white transition-colors">About Us <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {data.testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 md:py-28">
          <div className="text-center mb-14"><span className="text-sm uppercase tracking-widest text-emerald-700 font-semibold">Testimonials</span><h2 className="text-3xl sm:text-4xl mt-2 text-emerald-950">What Our Clients Say</h2></div>
          <div className="grid gap-7 md:grid-cols-2">
            {data.testimonials.map((t) => (
              <blockquote key={t.id} className="bg-white rounded-3xl p-8 border border-stone-200/80 shadow-sm">
                <Quote className="text-emerald-300 mb-4" size={32} />
                <p className="text-stone-700 italic leading-relaxed mb-5">{t.quote}</p>
                <footer className="font-semibold text-emerald-800">— {t.author}</footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {/* Media */}
      {data.media.length > 0 && (
        <section className="bg-stone-100/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
            <div className="text-center mb-12"><span className="text-sm uppercase tracking-widest text-emerald-700 font-semibold">In the Media</span><h2 className="text-3xl sm:text-4xl mt-2 text-emerald-950">Featured Coverage</h2></div>
            <div className="grid gap-7 md:grid-cols-2">
              {data.media.map((m) => (
                <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer" className="group flex gap-5 bg-white rounded-2xl p-5 border border-stone-200/80 hover:shadow-md transition-all">
                  <img src={m.image} alt={m.outlet} loading="lazy" className="w-28 h-28 object-cover rounded-xl flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                  <div><span className="text-xs font-mono uppercase text-emerald-700">{m.outlet}</span><p className="font-serif text-lg text-emerald-900 mt-1 group-hover:text-emerald-700">{m.title}</p><span className="text-sm text-[#C85A32] font-semibold mt-2 inline-block">Read Article →</span></div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest recipes & blog */}
      {(data.recipes.length > 0 || data.posts.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center justify-between mb-6"><h2 className="text-2xl sm:text-3xl text-emerald-950">Healthy Recipes</h2><Link to="/recipes" className="text-sm font-semibold text-emerald-700">View all →</Link></div>
              <div className="space-y-4">{data.recipes.map((r) => <Link key={r.id} to={`/recipes/${r.slug}`} className="flex gap-4 items-center bg-white rounded-2xl p-3 border border-stone-200/80 hover:shadow-md transition-all"><img src={r.featured_image} alt={r.title} loading="lazy" className="w-20 h-20 rounded-xl object-cover" /><div><p className="font-serif text-lg text-emerald-900">{r.title}</p><span className="text-xs text-stone-500">{(r.categories || []).join(", ")}</span></div></Link>)}</div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-6"><h2 className="text-2xl sm:text-3xl text-emerald-950">From the Blog</h2><Link to="/blog" className="text-sm font-semibold text-emerald-700">View all →</Link></div>
              <div className="space-y-4">{data.posts.map((p) => <Link key={p.id} to={`/blog/${p.slug}`} className="flex gap-4 items-center bg-white rounded-2xl p-3 border border-stone-200/80 hover:shadow-md transition-all"><img src={p.featured_image} alt={p.title} loading="lazy" className="w-20 h-20 rounded-xl object-cover" /><div><p className="font-serif text-lg text-emerald-900">{p.title}</p><span className="text-xs text-stone-500">{p.excerpt}</span></div></Link>)}</div>
            </div>
          </div>
        </section>
      )}

      {/* Closing call-to-action (editable via Homepage Hero manager → Closing CTA) */}
      {(() => {
        const d = { heading: "Ready to start a new healthy you?", subtitle: "Order products by phone or book your consultation today.",
          visible: true, primary: { visible: true, label: `Call to Order ${phone}`, url: `tel:${phoneTel}` },
          secondary: { visible: true, label: "Book an Appointment", url: "/contact" }, ...(data.settings?.home_cta || {}) };
        if (d.visible === false) return null;
        const Btn = (b, variant) => {
          if (!b || b.visible === false || !b.label) return null;
          const cls = variant === "primary"
            ? "bg-[#C85A32] text-white hover:bg-[#b24d28]"
            : "bg-amber-50 text-emerald-950 hover:bg-white";
          const common = `inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-colors ${cls}`;
          const url = b.url || "#";
          const isTel = url.startsWith("tel:");
          const external = /^(https?:|tel:|mailto:)/.test(url);
          const inner = <>{isTel && <Phone size={18} />} {b.label}</>;
          return external
            ? <a href={url} data-testid={`home-cta-${variant}`} className={common}>{inner}</a>
            : <Link to={url} data-testid={`home-cta-${variant}`} className={common}>{inner}</Link>;
        };
        return (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-24" data-testid="home-closing-cta">
            <div className="bg-gradient-to-r from-emerald-900 to-emerald-950 text-amber-50 rounded-3xl p-10 md:p-16 text-center shadow-xl border border-emerald-700/30">
              {d.heading && <h2 className="text-3xl md:text-4xl text-amber-50 mb-3">{d.heading}</h2>}
              {d.subtitle && <p className="text-stone-200 max-w-xl mx-auto mb-8">{d.subtitle}</p>}
              <div className="flex flex-wrap gap-4 justify-center">
                {Btn(d.primary, "primary")}
                {Btn(d.secondary, "secondary")}
              </div>
            </div>
          </section>
        );
      })()}
    </div>
  );
}
